<script setup>
import { useModal } from 'vue-final-modal';

import { seatsLimit } from '@/constants/form-find-tickets';
import analyticEventKeys from '@/constants/analytic-event-keys';

const props = defineProps({
  // all search filters
  modelValue: {
    type: Object,
    default: () => ({}),
  },

  buttonTextKey: {
    type: String,
    default: 'form.searchTickets',
  },
  isFlowFinish: {
    type: Boolean,
    default: false,
  },
  isFlowRunning: {
    type: Boolean,
    default: false,
  },

  hideButton: {
    type: Boolean,
    default: false,
  },
});
const emit = defineEmits(['update:modelValue', 'success']);

const { t } = useI18n();

const analytics = useGoogleAnalytics();

const buttonText = computed(() => {
  switch (true) {
    case !!props.isFlowRunning && !!props.isFlowFinish:
      return t('form.searchTickets');
    case !!props.isFlowRunning && !props.isFlowFinish:
      return t('form.next');
    default:
      return t('form.set');
  }
});

// models
const adultsModel = computed({
  get() {
    return props.modelValue.adults;
  },
  set(value) {
    emit('update:modelValue', { ...props.modelValue, adults: value });
    analytics.pushEvent({
      event: analyticEventKeys.SEARCH_FORM_INTERACTION,
      label: `input: seats adult`,
    });
  },
});
const childrenModel = computed({
  get() {
    return props.modelValue.children;
  },
  set(value) {
    emit('update:modelValue', { ...props.modelValue, children: value });
    analytics.pushEvent({
      event: analyticEventKeys.SEARCH_FORM_INTERACTION,
      label: `input: seats children`,
    });
  },
});
const infantsModel = computed({
  get() {
    return props.modelValue.infants;
  },
  set(value) {
    emit('update:modelValue', { ...props.modelValue, infants: value });
    analytics.pushEvent({
      event: analyticEventKeys.SEARCH_FORM_INTERACTION,
      label: `input: seats infants`,
    });
  },
});
const seatsForInfantsModel = computed({
  get() {
    return props.modelValue.seatsForInfants;
  },
  set(value) {
    emit('update:modelValue', { ...props.modelValue, seatsForInfants: value });
  },
});

const totalSeatsCount = computed(
  () => adultsModel.value + childrenModel.value + infantsModel.value,
);

watch(infantsModel, (value, oldValue) => {
  if ((!oldValue || oldValue < 1) && value > 0) {
    seatsForInfantsModel.value = true;
  } else if (value === 0) {
    seatsForInfantsModel.value = false;
  }
});
watch(seatsForInfantsModel, (value) => {
  if (value && infantsModel.value < 1) infantsModel.value = 1;
});

// modal
const seatsInfoModal = useModal({
  component: resolveComponent('LazyModalsSeatsInfo'),
});
</script>

<template>
  <div class="bundles-seats-select">
    <div
      class="bundles-seats-select__wrapper bundles-seats-select__wrapper--bottom"
    >
      <div class="bundles-seats-select__counters">
        <UiCounter
          v-model="adultsModel"
          class="bundles-seats-select__counter"
          icon-name="user"
          :title="t('form.adult')"
          description="12+"
          :min="1"
          :max="seatsLimit"
          :total-count="totalSeatsCount"
        />
        <UiCounter
          v-model="childrenModel"
          class="bundles-seats-select__counter"
          icon-name="child"
          :title="t('form.children')"
          :max="seatsLimit"
          :total-count="totalSeatsCount"
          description="3-11"
        />
        <UiCounter
          v-model="infantsModel"
          class="bundles-seats-select__counter"
          icon-name="infant"
          :title="t('form.infants')"
          :max="seatsLimit"
          :total-count="totalSeatsCount"
          description="0-2"
          with-question
          @question-click="seatsInfoModal.open"
        />
        <FormFieldCheckbox
          v-model="seatsForInfantsModel"
          class="bundles-seats-select__checkbox"
          name="seats"
        >
          <span
            class="bundles-seats-select__font bundles-seats-select__font--checkbox"
          >
            {{ t('form.addInfantsSeats') }}
          </span>
        </FormFieldCheckbox>
      </div>

      <UiButton
        v-if="!hideButton"
        class="bundles-seats-select__button"
        theme="secondary"
        @click="emit('success')"
      >
        <span
          class="bundles-seats-select__font bundles-seats-select__font--button"
        >
          {{ buttonText }}
        </span>
      </UiButton>
    </div>
  </div>
</template>

<style scoped lang="scss">
.bundles-seats-select {
  display: flex;
  flex-direction: column;

  &__font {
    display: block;

    &--checkbox {
      font-size: em(14);
      line-height: math.div(18, 20);
    }

    &--button {
      font-size: em(20);
      font-weight: 500;
      line-height: math.div(22, 20);
    }
  }

  &__wrapper {
    flex-shrink: 0;

    &--bottom {
      display: flex;
      flex-direction: column;
      gap: em(20);
      width: 100%;
      margin-top: auto;
    }
  }

  &__counters {
    display: flex;
    flex-direction: column;
    gap: em($gap-sm);
  }

  &__button {
    height: em(63);
  }
}
</style>
